import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { type ProfileType } from '@rma/common-ui-domain/types';
import { ProfileAvatarComponent } from '@rma/profiles/public/ui-profile-avatar';

export interface AvatarStackProfile {
  avatarUrl: string;
  profileName: string;
  profileType: ProfileType;
}

const avatarSizeConfig = {
  default: 'h-10 w-10',
  large: 'h-14 w-14',
} as const;

type AvatarSize = keyof typeof avatarSizeConfig;

@Component({
  selector: 'rma-avatar-stack',
  imports: [MatTooltipModule, NgClass, ProfileAvatarComponent],
  templateUrl: './avatar-stack.component.html',
  host: {
    class: 'flex',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AvatarStackComponent {
  public readonly profiles = input<AvatarStackProfile[]>([]);
  public readonly max = input<number>();
  public readonly displayProfileTypes = input<ProfileType | ProfileType[] | null | undefined>(null);
  public readonly size = input<AvatarSize>('default');

  public readonly displayProfiles = computed(() => {
    const profiles = this.profiles();
    const displayProfileTypes = this.displayProfileTypes();

    const profileTypes = Array.isArray(displayProfileTypes) ? displayProfileTypes : displayProfileTypes ? [displayProfileTypes] : undefined;
    return profileTypes ? profiles.filter((x) => profileTypes.includes(x.profileType)) : profiles;
  });

  public readonly avatarStackProfiles = computed(() => {
    const max = this.max();
    return max ? this.displayProfiles().slice(0, max) : this.displayProfiles();
  });

  public readonly avatarSize = computed(() => `-ml-2 rounded-full border-2 border-white ${avatarSizeConfig[this.size()]}`);

  public readonly hasMore = computed(() => {
    const max = this.max();
    return max && this.displayProfiles().length > max;
  });

  public readonly more = computed(() => this.displayProfiles().length - (this.max() ?? 6));
}
